import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import { Button } from "../";
import WorkTimezoneSelect from "../WorkTimezoneSelect";
import API from "../../utils/api";
import { Toaster } from "../../utils/common";
import {
  MAX_LOGIN_CYCLES,
  addDaysYmd,
  canPunchOnCalendarDay,
  computeSessionTimerFromEvents,
  countCompletedLogoutCycles,
  countLoginEvents,
  formatElapsedHMS,
  getLocalYMD,
  getWorkingHoursTimeZone,
  isLoginEventType,
  isLogoutEventType,
  normalizeEvents,
  resolveRowEvents,
  resolveWorkDateYmd,
  rowHasOpenLogin,
} from "../../utils/workingHoursPunch";
import {
  getSavedWorkTimeZone,
  persistWorkTimeZonePreference,
} from "../../utils/workTimezonePreference";
import "../layout/logoutModal.css";
import "./DashboardLoginCard.scss";

/**
 * Compact Login / Logout + live session timer for the staff dashboard.
 */
export default function DashboardLoginCard() {
  const [loading, setLoading] = useState(false);
  const [punching, setPunching] = useState(false);
  const [mode, setMode] = useState("Office");
  const [todayRow, setTodayRow] = useState(null);
  const [localEvents, setLocalEvents] = useState([]);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [showLimitTip, setShowLimitTip] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [workTimezone, setWorkTimezone] = useState(() => getSavedWorkTimeZone());
  const fetchedYmdRef = useRef(getLocalYMD());

  const modeOptions = useMemo(
    () => [
      { id: "Office", value: "Office" },
      { id: "WFH", value: "WFH" },
    ],
    [],
  );

  const todayYmd = getLocalYMD(new Date(nowTick));

  const events = useMemo(() => {
    const fromApi = resolveRowEvents(todayRow);
    const merged = (() => {
      if (!localEvents.length) return fromApi;
      const seen = new Set();
      return [...fromApi, ...localEvents]
        .filter((e) => e?.type && e?.at)
        .sort((a, b) => new Date(a.at) - new Date(b.at))
        .filter((e) => {
          const key = `${String(e.type).toLowerCase()}|${new Date(e.at).toISOString()}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
    })();
    // Keep punches stored on this work day. Re-filtering by the clock date
    // drops a login when IST and US are on different calendar dates.
    return merged;
  }, [todayRow, localEvents]);

  const fetchToday = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch ±1 day so timezone-shifted work_date rows still come back.
      // (Dashboard previously used only local today, which missed the open session
      // HRMS shows when server date and local date differ.)
      const today = getLocalYMD();
      const from = addDaysYmd(today, -1);
      const to = addDaysYmd(today, 1);
      const { data } = await API.get(
        `/working-hours/my?from=${from}&to=${to}`,
      );
      const list = data?.data || data || [];
      const rows = Array.isArray(list) ? list : [];

      // Only use today's calendar work_date for the punch card.
      // Yesterday's forgotten open login must not block or appear as "logged in".
      const sameDayRows = rows.filter((r) => resolveWorkDateYmd(r) === today);
      const loginIsToday = (row) => {
        const evs = normalizeEvents(row?.events);
        const last = evs[evs.length - 1];
        return isLoginEventType(last?.type) && getLocalYMD(last.at) === today;
      };

      const mine =
        sameDayRows.find((r) => rowHasOpenLogin(r)) ||
        rows.find((r) => loginIsToday(r)) ||
        sameDayRows[0] ||
        null;

      setTodayRow(mine);
      setLocalEvents([]);
      const rowMode = mine?.mode || mine?.work_mode;
      if (rowMode === "Office" || rowMode === "WFH") {
        setMode(rowMode);
      }
    } catch (e) {
      setTodayRow(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchToday();
  }, [fetchToday]);

  const nextPunch = useMemo(() => {
    if (!events.length) return "login";
    const last = events[events.length - 1];
    if (isLoginEventType(last?.type)) return "logout";
    if (isLogoutEventType(last?.type)) return "login";
    return events.length % 2 === 0 ? "login" : "logout";
  }, [events]);

  const loginLimitReached = useMemo(() => {
    return (
      countCompletedLogoutCycles(events) >= MAX_LOGIN_CYCLES ||
      countLoginEvents(events) >= MAX_LOGIN_CYCLES
    );
  }, [events]);

  const { elapsedMs, activeLoginAtMs, hasOpenLoginForgotten } = useMemo(() => {
    return computeSessionTimerFromEvents(events, nowTick, {
      workDateYmd: todayYmd,
    });
  }, [events, nowTick, todayYmd]);

  const isLoggedIn = Boolean(activeLoginAtMs);

  const effectiveNextPunch = useMemo(() => {
    if (activeLoginAtMs) return "logout";
    if (hasOpenLoginForgotten) return "login";
    return nextPunch;
  }, [activeLoginAtMs, hasOpenLoginForgotten, nextPunch]);

  useEffect(() => {
    setNowTick(Date.now());
    const id = setInterval(
      () => setNowTick(Date.now()),
      isLoggedIn ? 1000 : 15000,
    );
    return () => clearInterval(id);
  }, [isLoggedIn, activeLoginAtMs]);

  useEffect(() => {
    // Reload once when the work-timezone date changes. Comparing the row date
    // refetched on every response whenever that date was not exactly today.
    if (!todayYmd || fetchedYmdRef.current === todayYmd) return;
    fetchedYmdRef.current = todayYmd;
    fetchToday();
  }, [todayYmd, fetchToday]);

  const sessionElapsedLabel = useMemo(() => {
    return formatElapsedHMS(elapsedMs);
  }, [elapsedMs]);

  const canPunch = useMemo(
    () => canPunchOnCalendarDay(todayRow),
    [todayRow],
  );

  const timezoneReady = Boolean(workTimezone);

  const handleWorkTimezoneChange = async (nextTz) => {
    if (!nextTz || nextTz === workTimezone) return;
    const { timezone } = await persistWorkTimeZonePreference(nextTz);
    if (!timezone) return;
    setWorkTimezone(timezone);
    await fetchToday();
  };

  const loginLimitMessage =
    "You have logged in and logged out 3 times already. Further login is not allowed for today.";

  const punch = async (type) => {
    if (!timezoneReady) {
      Toaster.warning("Select your work timezone first. You can change it later if your work region changes.");
      return;
    }
    if (!canPunch) {
      Toaster.warning("Punching is only available for today's work date");
      return;
    }
    if (type === "login" && loginLimitReached) {
      setShowLimitTip(true);
      Toaster.warning(loginLimitMessage);
      return;
    }
    if (type === "login" && effectiveNextPunch !== "login") {
      Toaster.warning("You are already logged in. Please logout first.");
      return;
    }
    if (type === "logout" && effectiveNextPunch !== "logout") {
      Toaster.warning("Please login first");
      return;
    }
    setPunching(true);
    try {
      const res = await API.post("/working-hours/punch", {
        type,
        mode,
        // Work-region calendar day + IANA zone (not OS-local getDate()).
        work_date: getLocalYMD(),
        timezone: getWorkingHoursTimeZone(),
      });
      if (res?.data?.status === 0) {
        Toaster.warning(res.data.message || "Could not update working hours");
        // Refresh so UI matches backend (e.g. already logged in).
        await fetchToday();
        return;
      }

      const payload = res?.data?.data;
      const payloadEvents =
        payload?.events || payload?.working_events || payload?.logs || null;

      if (Array.isArray(payloadEvents) && payloadEvents.length) {
        setLocalEvents(normalizeEvents(payloadEvents));
        setTodayRow((prev) => ({ ...(prev || {}), ...(payload || {}) }));
      } else {
        const nowIso = new Date().toISOString();
        setLocalEvents((prev) => [...prev, { type, at: nowIso, mode }]);
      }

      await fetchToday();
    } catch (e) {
      const msg =
        e?.response?.data?.message || "Failed to update working hours";
      Toaster.error(msg);
      await fetchToday();
    } finally {
      setPunching(false);
    }
  };

  return (
    <div className="dash-login-card" aria-label="Working hours login">
      <div className="dash-login-card__head">
        <div className="dash-login-card__timer-line">
          <span className="dash-login-card__clock" aria-hidden="true">
            <AccessTimeIcon fontSize="small" />
          </span>
          <span className="dash-login-card__time">{sessionElapsedLabel}</span>
        </div>
        <div className="dash-login-card__selects">
          <select
            className="dash-login-card__select"
            aria-label="Work mode"
            value={mode}
            disabled={isLoggedIn || punching || loading}
            onChange={(e) => setMode(e.target.value)}
          >
            {modeOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.value}
              </option>
            ))}
          </select>
          <WorkTimezoneSelect
            className="dash-login-card__select"
            value={workTimezone}
            compact
            disabled={isLoggedIn || punching || loading}
            onChange={handleWorkTimezoneChange}
          />
        </div>
      </div>
      <div className="dash-login-card__actions">
        <div
          className={`dash-login-card__login-wrap${
            loginLimitReached && !isLoggedIn
              ? " dash-login-card__login-wrap--limited"
              : ""
          }`}
          onMouseEnter={() => {
            if (loginLimitReached && !isLoggedIn) setShowLimitTip(true);
          }}
          onMouseLeave={() => setShowLimitTip(false)}
          onFocus={() => {
            if (loginLimitReached && !isLoggedIn) setShowLimitTip(true);
          }}
          onBlur={() => setShowLimitTip(false)}
          onClick={() => {
            if (!(loginLimitReached && !isLoggedIn)) return;
            setShowLimitTip(true);
            Toaster.warning(loginLimitMessage);
          }}
        >
          {showLimitTip && loginLimitReached && !isLoggedIn ? (
            <div className="dash-login-card__limit-tip" role="tooltip">
              {loginLimitMessage}
            </div>
          ) : null}
          <Button
            ui="primary"
            text="Login"
            disable={
              loading ||
              punching ||
              isLoggedIn ||
              !canPunch ||
              loginLimitReached ||
              !timezoneReady
            }
            handleClick={() => punch("login")}
          />
        </div>
        <Button
          // ui="transparent"
          text="Logout"
          disable={loading || punching || !isLoggedIn || !canPunch}
          handleClick={() => setLogoutConfirmOpen(true)}
        />
      </div>

      {logoutConfirmOpen ? (
        <div
          className="logout-modal-overlay"
          onClick={() => setLogoutConfirmOpen(false)}
        >
          <div className="logout-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="logout-modal-title">Logout</h2>
            <p className="logout-modal-question">
              Are you sure you want to logout?
            </p>
            <div className="logout-modal-buttons">
              <button
                type="button"
                className="logout-modal-btn logout-modal-btn-no"
                onClick={() => setLogoutConfirmOpen(false)}
              >
                No
              </button>
              <button
                type="button"
                className="logout-modal-btn logout-modal-btn-yes"
                onClick={() => {
                  setLogoutConfirmOpen(false);
                  punch("logout");
                }}
              >
                Yes, Logout
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
